import debug_pkg from "debug";
import { err, ok, Result, safeTry } from "neverthrow";
import { match, P } from "ts-pattern";

import {
  buildInvalidError,
  defined,
  IncidentWorkItemStatusSchema,
  parsed,
  type InvalidError,
} from "@thalamos/common";

import {
  FormSectionSchema,
  IncidentFormIdSchema,
  IncidentFormTypeSchema,
  IncidentTypeSchema,
  ParticipantStatusSchema,
  UserOrganisationRoleSchema,
  type CreateIncidentMinimumFields,
  type ExtendedVisionUser,
  type IncidentFormSections,
  type IncidentWorkItemWithContext,
  type McaPersonDetailsMinimumFields,
  type MinimumFieldsPerSection,
  type PersonDetailsMinimumFields,
  type RecordPlaceOfSafetyMinimumFields,
  type TriageIncidentMinimumFields,
} from "../types/index.js";
import { workflows } from "../workflowDefinitions/workflows.js";
import type { ActionInput } from "./ActionInput.js";
import { getLatestPolicePresenceUpdate } from "./actions/policePresenceUpdateDue.js";
import { calculateIncidentWorkItemStatus } from "./calculateIncidentWorkItemStatus.js";
import { combinePlacesOfSafetyAndOfficerPresenceForms } from "./combinePlacesOfSafetyAndOfficerPresenceForms.js";
import { getLatestPlaceOfSafetyForm } from "./getLatestPlaceOfSafety.js";
import { isCurrentOwner } from "./helpers/currentOwner.js";
import { getIncidentTypeSelection } from "./helpers/getIncidentTypeSelection.js";

export * from "./helpers/index.js";

const { debug: debugFn } = debug_pkg;
const debug = debugFn("vision-common:workflowLogic:helpers");

export function incidentIsActive({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">): boolean {
  return match(
    calculateIncidentWorkItemStatus(incidentWorkItem).unwrapOr(
      IncidentWorkItemStatusSchema.enum.unknown,
    ),
  )
    .with("ended", "unknown", () => false)
    .with(
      "in-progress",
      "awaiting-arrival",
      "arrived-at-place-of-safety",
      "form-shared",
      "nhs-form-accepted",
      "police-presence-not-requested",
      "police-presence-requested",
      () => true,
    )
    .exhaustive();
}

/**
 * Whether the triage section holds the answers it is never allowed to omit.
 *
 * Narrower than "the triage is valid": the schema is built with no form values,
 * so conditionally-visible fields resolve to hidden and accept null. What is
 * asserted is that the always-visible mandatory answers are present.
 */
export function hasTriageForm({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">): boolean {
  const result = safeTry(function* () {
    const triageForm = yield* defined(
      incidentWorkItem.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.triage,
      ),
    );

    const workflow = yield* defined(
      workflows[incidentWorkItem.incidentWorkItem.workflowVersion],
    );

    const triageSchema = yield* defined(
      workflow.triageIncident.formDefinition.sections
        .find((section) =>
          match(section.id)
            .with(FormSectionSchema.enum.triageMain, () => true)
            .exhaustive(),
        )
        ?.buildFormSpecification({}).formSchema,
    );

    const triageSection = yield* defined(triageForm.sections[0]);

    return parsed(triageSchema, triageSection.data).map(() => true);
  });

  return result.unwrapOr(false);
}

export function hasSection136Form({ incidentWorkItem }: ActionInput): boolean {
  return (
    incidentWorkItem.forms.find(
      (form) => form.formType === IncidentFormTypeSchema.enum.section136,
    ) !== undefined
  );
}

export function hasMcaForm({ incidentWorkItem }: ActionInput): boolean {
  return (
    incidentWorkItem.forms.find(
      (form) => form.formType === IncidentFormTypeSchema.enum.mentalCapacityAct,
    ) !== undefined
  );
}

export function hasPlaceOfSafety({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">): boolean {
  return (
    incidentWorkItem.forms.find(
      (form) =>
        form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
    ) !== undefined
  );
}

export function userIsIncidentOwner({
  incidentWorkItem,
  user,
}: ActionInput): boolean {
  return isCurrentOwner(incidentWorkItem, user.id);
}

export function userIsThalamosAdmin({ user }: ActionInput): boolean {
  return user.isAdminSession;
}

export function isCurrentUserSupervisor(user: ExtendedVisionUser): boolean {
  return (
    user.sessionContext?.role === UserOrganisationRoleSchema.enum.Supervisor
  );
}

export function getArrivalSectionData(
  incidentWorkItem: IncidentWorkItemWithContext,
) {
  const posFormResult = getLatestPlaceOfSafetyForm(incidentWorkItem);
  const posForm = posFormResult.isOk() ? posFormResult.value : undefined;

  const formSectionArrival =
    posForm?.sections.find((section) => {
      return section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival;
    })?.data ?? null;

  return formSectionArrival;
}

export function hasArrivedAtLatestPlaceOfSafety({
  incidentWorkItem,
}: ActionInput): boolean {
  const result = safeTry(function* () {
    const formSectionArrival = getArrivalSectionData(incidentWorkItem);

    const workflow = yield* defined(
      workflows[incidentWorkItem.incidentWorkItem.workflowVersion],
    );

    const sectionSchema = yield* defined(
      workflow.recordPlaceOfSafety.formDefinition.sections
        .find(
          (section) =>
            section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
        )
        ?.buildFormSpecification({}).formSchema,
    );

    const validatedSectionData = (yield* parsed(
      sectionSchema,
      formSectionArrival,
    ).orTee(
      debug,
    )) as MinimumFieldsPerSection[typeof FormSectionSchema.enum.recordPlaceOfSafetyArrival];

    const arrivalCompleted = validatedSectionData.arrivalDateTime !== null;

    return ok(arrivalCompleted);
  });

  return result.unwrapOr(false);
}

function section136SectionComplete(
  incidentWorkItem: IncidentWorkItemWithContext,
  sectionId: IncidentFormSections[typeof IncidentFormTypeSchema.enum.section136],
): boolean {
  const result = safeTry(function* () {
    const form = yield* defined(
      incidentWorkItem.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.section136,
      ),
    );

    const section = yield* defined(
      form.sections.find((section) => section.id === sectionId),
    );

    const workflow = yield* defined(
      workflows[incidentWorkItem.incidentWorkItem.workflowVersion],
    );

    const sectionSchema = yield* defined(
      workflow["136"].formDefinition.sections
        .find((section) => section.id === sectionId)
        ?.buildFormSpecification({}).formSchema,
    );

    return parsed(sectionSchema, section.data).map(() => true);
  });

  return result.unwrapOr(false);
}

function mcaSectionComplete(
  incidentWorkItem: IncidentWorkItemWithContext,
  sectionId: IncidentFormSections[typeof IncidentFormTypeSchema.enum.mentalCapacityAct],
): boolean {
  const result = safeTry(function* () {
    const form = yield* defined(
      incidentWorkItem.forms.find(
        (form) =>
          form.formType === IncidentFormTypeSchema.enum.mentalCapacityAct,
      ),
    );

    const section = yield* defined(
      form.sections.find((section) => section.id === sectionId),
    );

    const workflow = yield* defined(
      workflows[incidentWorkItem.incidentWorkItem.workflowVersion],
    );

    const sectionSchema = yield* defined(
      workflow["mentalCapacityAct"].formDefinition.sections
        .find((section) => section.id === sectionId)
        ?.buildFormSpecification({}).formSchema,
    );

    return parsed(sectionSchema, section.data).map(() => true);
  });

  return result.unwrapOr(false);
}

export function section136FormCompleted(actionInput: ActionInput): boolean {
  const sectionIds = [
    FormSectionSchema.enum.personDetails,
    FormSectionSchema.enum.detentionDetails,
    FormSectionSchema.enum.informationForHealthcare,
  ] as const satisfies IncidentFormSections[typeof IncidentFormTypeSchema.enum.section136][];

  return sectionIds.every((sectionId) =>
    section136SectionComplete(actionInput.incidentWorkItem, sectionId),
  );
}

export function mcaFormCompleted(actionInput: ActionInput): boolean {
  const sectionIds = [
    FormSectionSchema.enum.mcaPersonDetails,
    FormSectionSchema.enum.mcaIncidentDetails,
    FormSectionSchema.enum.mcaCapacityAssessment,
    FormSectionSchema.enum.mcaAccompaniment,
  ] as const satisfies IncidentFormSections[typeof IncidentFormTypeSchema.enum.mentalCapacityAct][];

  return sectionIds.every((sectionId) =>
    mcaSectionComplete(actionInput.incidentWorkItem, sectionId),
  );
}

export function incidentHasTransferRequest({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">): boolean {
  const pendingTransfer = incidentWorkItem.participants.some((participant) => {
    return participant.status === ParticipantStatusSchema.enum.pendingTransfer;
  });
  return pendingTransfer;
}

/**
 * An incident should have exactly one of Section 136 or MCA form.
 *
 * @param incidentWorkItem - The incident work item whose forms are inspected to determine the selected form type.
 * @returns Result with "section136" or "mca", or an error if both or neither exist
 */
export function getTriageIncidentSelectedFormType({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">): Result<
  "section136" | "mca",
  InvalidError
> {
  const section136Form = incidentWorkItem.forms.find(
    (form) => form.formType === IncidentFormTypeSchema.enum.section136,
  );
  const mcaForm = incidentWorkItem.forms.find(
    (form) => form.formType === IncidentFormTypeSchema.enum.mentalCapacityAct,
  );

  return match([section136Form, mcaForm] as const)
    .with([P.not(P.nullish), P.not(P.nullish)], () =>
      err(
        buildInvalidError({
          message: "Incident cannot have both Section 136 and MCA forms",
        }),
      ),
    )
    .with([P.not(P.nullish), P.nullish], () => ok("section136" as const))
    .with([P.nullish, P.not(P.nullish)], () => ok("mca" as const))
    .with([P.nullish, P.nullish], () =>
      err(
        buildInvalidError({
          message: "Incident must have either a Section 136 or MCA form",
        }),
      ),
    )
    .exhaustive();
}

/**
 * The section of the incident's selected form in which the officer records
 * who the person is.
 *
 * @returns undefined unless exactly one of the Section 136 and MCA forms
 * exists and its person-details section has been saved
 */
export function getPersonDetailsSection({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">):
  PersonDetailsMinimumFields | McaPersonDetailsMinimumFields | undefined {
  return getTriageIncidentSelectedFormType({ incidentWorkItem })
    .map((formType) => {
      const { targetFormType, sectionId } = match(formType)
        .with("section136", () => ({
          targetFormType: IncidentFormTypeSchema.enum.section136,
          sectionId: FormSectionSchema.enum.personDetails,
        }))
        .with("mca", () => ({
          targetFormType: IncidentFormTypeSchema.enum.mentalCapacityAct,
          sectionId: FormSectionSchema.enum.mcaPersonDetails,
        }))
        .exhaustive();
      return incidentWorkItem.forms
        .find((form) => form.formType === targetFormType)
        ?.sections.find((section) => section.id === sectionId)?.data;
    })
    .unwrapOr(undefined) as
    PersonDetailsMinimumFields | McaPersonDetailsMinimumFields | undefined;
}

export function getPatientName({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">): {
  fullName: string;
  forename?: string;
  surname?: string;
} {
  const createIncidentFormSection = (incidentWorkItem.forms
    .find(
      (form) => form.formType === IncidentFormTypeSchema.enum.createIncident,
    )
    ?.sections.find(
      (section) => section.id === FormSectionSchema.enum.createIncidentMain,
    )?.data ?? undefined) as CreateIncidentMinimumFields | undefined;

  // Falls back to createIncident until the selected form's person details exist
  const personDetailsFormSection = getPersonDetailsSection({
    incidentWorkItem,
  });

  const forename = match(
    personDetailsFormSection?.personForename ??
      createIncidentFormSection?.personForename,
  )
    .with({ value: P.string, isUnknown: false }, (forename) => forename.value)
    .otherwise(() => undefined);

  const surname = match(
    personDetailsFormSection?.personSurname ??
      createIncidentFormSection?.personSurname,
  )
    .with({ value: P.string, isUnknown: false }, (surname) => surname.value)
    .otherwise(() => undefined);

  const fullName = match([forename, surname])
    .with([undefined, undefined], ([_forename, _surname]) => "Unknown") // TODO: Can we get this from the translation library? Perhaps inject it into this function?
    .with([P.string, undefined], ([forename, _surname]) => forename)
    .with([undefined, P.string], ([_forename, surname]) => surname)
    .with([P.string, P.string], ([forename, surname]) =>
      [forename, surname].join(" "),
    )
    .exhaustive();

  return { fullName, forename, surname };
}

export function getCommandUnit({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">):
  | {
      id: string; // TODO: This should be a branded type
      name: string;
    }
  | undefined {
  const createIncidentFormSection = (incidentWorkItem.forms
    .find(
      (form) => form.formType === IncidentFormTypeSchema.enum.createIncident,
    )
    ?.sections.find(
      (section) => section.id === FormSectionSchema.enum.createIncidentMain,
    )?.data ?? undefined) as CreateIncidentMinimumFields | undefined;

  return createIncidentFormSection
    ? {
        id: createIncidentFormSection.commandUnit.value,
        name: createIncidentFormSection.commandUnit.valueLabel,
      }
    : undefined;
}

const getLatestPlaceOfSafetyDetails = (
  incidentWorkItem: IncidentWorkItemWithContext | undefined,
) => {
  return safeTry(function* () {
    const posForm = yield* getLatestPlaceOfSafetyForm(incidentWorkItem);

    const posFormSectionMain = yield* defined(
      posForm.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.recordPlaceOfSafetyMain,
      ),
      "A Place Of Safety form exists, but is missing a mandatory section - recordPlaceOfSafetyMain",
    );

    const posFormSectionArrival = yield* defined(
      posForm.sections.find(
        (section) =>
          section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
      ),
      "A Place Of Safety form exists, but is missing a mandatory section - recordPlaceOfSafetyArrival",
    );

    const posFormSectionArrivalId = yield* parsed(
      IncidentFormIdSchema,
      posFormSectionArrival.incidentFormId,
    );

    const posName = yield* defined(
      (
        (posFormSectionMain.data ?? undefined) as
          RecordPlaceOfSafetyMinimumFields | undefined
      )?.placeOfSafety.name,
      "posName undefined",
    );

    const associatedEmails = (
      (posFormSectionMain.data ?? undefined) as
        RecordPlaceOfSafetyMinimumFields | undefined
    )?.placeOfSafety.associatedEmails;

    // To improve this further we could split this into two functions, one for the name and one for the id
    return ok({
      name: posName,
      arrivalFormId: posFormSectionArrivalId,
      associatedEmails: associatedEmails,
    });
  });
};

// This gets just the name of the latest place of safety
export const getLatestPlaceOfSafetyName = (
  incidentWorkItem: IncidentWorkItemWithContext | undefined,
) => {
  return getLatestPlaceOfSafetyDetails(incidentWorkItem)
    .orTee(debug)
    .unwrapOr(undefined)?.name;
};

// This gets full object: { name, arrivalFormId } of the latest place of safety
export const getLatestPlaceOfSafety = (
  incidentWorkItem: IncidentWorkItemWithContext | undefined,
) => {
  return getLatestPlaceOfSafetyDetails(incidentWorkItem)
    .orTee(debug)
    .unwrapOr(undefined);
};

export function triageSelectionIsSection136({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">): boolean {
  const result = safeTry(function* () {
    const triageForm = yield* defined(
      incidentWorkItem.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.triage,
      ),
    );

    const triageMainSectionData = yield* defined(
      triageForm.sections.find((section) =>
        match(section.id)
          .with(FormSectionSchema.enum.triageMain, () => true)
          .otherwise(() => false),
      )?.data,
    );

    // Warning: This is a workaround to avoid the zod validation
    // We should fix this in the future to ensure that the data is always valid
    // Problem is because we sometimes anonymise the data its not always in the expected format
    // Ticket here to fix: ASP-3895
    const validatedTriageMainSectionData =
      triageMainSectionData as Partial<TriageIncidentMinimumFields>;

    return ok(
      validatedTriageMainSectionData.whatWillYouDo?.value ===
        IncidentTypeSchema.enum.section136 ||
        validatedTriageMainSectionData.whatWillYouDo?.value ===
          IncidentTypeSchema.enum.mentalCapacityAct,
    );
  });

  return result.unwrapOr(false);
}

export function triageSelectionIsMca({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">): boolean {
  const result = safeTry(function* () {
    const triageForm = yield* defined(
      incidentWorkItem.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.triage,
      ),
    );

    const triageMainSectionData = yield* defined(
      triageForm.sections.find((section) =>
        match(section.id)
          .with(FormSectionSchema.enum.triageMain, () => true)
          .otherwise(() => false),
      )?.data,
    );

    // Warning: This is a workaround to avoid the zod validation
    // We should fix this in the future to ensure that the data is always valid
    // Problem is because we sometimes anonymise the data its not always in the expected format
    // Ticket here to fix: ASP-3895
    const validatedTriageMainSectionData =
      triageMainSectionData as Partial<TriageIncidentMinimumFields>;

    return ok(
      validatedTriageMainSectionData.whatWillYouDo?.value ===
        IncidentTypeSchema.enum.mentalCapacityAct,
    );
  });

  return result.unwrapOr(false);
}

/**
 * Human-readable display names for incident types.
 * These are used in email templates and other non-i18n contexts.
 */
export const IncidentTypeDisplayName = {
  "136": "Section 136",
  mca: "MCA",
  voluntary: "Voluntary",
  other: "Other",
} as const;

/**
 * Gets the human-readable display name for an incident based on its triage selection.
 * This is used in email templates and other contexts where i18n is not available.
 *
 * @param incidentWorkItem - The incident work item containing triage form data
 * @returns The display name (e.g., "Section 136", "MCA") or "Section 136" as default
 */
export function getIncidentTypeDisplayName({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">): string {
  const incidentType = getIncidentTypeSelection({ incidentWorkItem })
    .orTee((e) => {
      console.error("Unsupported incident type", e.prettifyError());
    })
    .unwrapOr(undefined);
  return match(incidentType)
    .with(
      IncidentTypeSchema.enum.section136,
      () => IncidentTypeDisplayName["136"],
    )
    .with(
      IncidentTypeSchema.enum.mentalCapacityAct,
      () => IncidentTypeDisplayName.mca,
    )
    .with(
      IncidentTypeSchema.enum.voluntary,
      () => IncidentTypeDisplayName.voluntary,
    )
    .with(IncidentTypeSchema.enum.other, () => IncidentTypeDisplayName.other)
    .with(undefined, () => IncidentTypeDisplayName["136"])
    .exhaustive();
}

export function hasBeenAnonymised({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">): boolean {
  return incidentWorkItem.incidentWorkItem.anonymiserVersion !== null;
}

export function formIsAutoClosed({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">): boolean {
  const result = safeTry(function* () {
    const preEndIncidentForm = yield* defined(
      incidentWorkItem.forms.find((form) =>
        match(form.formType)
          .with(IncidentFormTypeSchema.enum.preEndIncident, () => true)
          .otherwise(() => false),
      ),
    );

    const preEndIncidentSections = yield* defined(
      preEndIncidentForm.sections.find((section) =>
        match(section.id)
          .with(FormSectionSchema.enum.preEndIncident, () => true)
          .otherwise(() => false),
      )?.data,
    );

    return ok(
      preEndIncidentSections.endingReason === "auto-close-by-anonymiser-job",
    );
  });

  return result.unwrapOr(false);
}

export const getLatestPolicePresence = (
  incidentWorkItem: IncidentWorkItemWithContext | undefined,
) => {
  return safeTry(function* () {
    // Ensure we have an incident work item
    const workItem = yield* defined(
      incidentWorkItem,
      "No incident work item provided",
    );

    // Get the latest combined Place of Safety + Police Presence info
    const latestPlaceOfSafety = combinePlacesOfSafetyAndOfficerPresenceForms(
      workItem,
    )
      .unwrapOr([])
      .at(0);

    // If none found, stop early
    if (!latestPlaceOfSafety) {
      return ok(undefined);
    }

    // Return the latest update info (createdAt + hasRemainedAtPlaceOfSafety)
    const latestUpdate = getLatestPolicePresenceUpdate({ latestPlaceOfSafety });

    return ok(latestUpdate);
  }).unwrapOr(undefined);
};
